import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Seo } from "@/components/layout/Seo";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { siteConfig } from "@/data/siteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-navy/20 px-4 py-3 text-sm text-navy placeholder:text-navy/35 focus:border-ser-purple focus:ring-1 focus:ring-ser-purple/20 focus:outline-none transition-colors";

export default function ContactPage() {
  const { lang, t } = useLang();
  const f = t.contactPage.form;
  const [form, setForm] = useState({
    first_name: "", last_name: "", organization: "", email: "", country: "", service: "", message: "", website: "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(f.success);
      setForm({ first_name: "", last_name: "", organization: "", email: "", country: "", service: "", message: "" });
    } catch {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo page="contact" />
      <section className="py-14 md:py-20" data-testid="contact-page">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>{t.contactPage.overline}</Overline>
              <h1 className="font-display text-4xl md:text-5xl leading-[1.08] tracking-tight text-navy mt-4">
                {t.contactPage.title}
              </h1>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-navy/70 max-w-md">{t.contactPage.text}</p>
              <span className="block h-px w-16 bg-ser-yellow mt-10" aria-hidden="true" />
              <ul className="mt-10 space-y-4">
                {siteConfig.emails.map((e) => (
                  <li key={e.key}>
                    <a href={`mailto:${e.address}`} className="text-sm font-medium text-ser-purple hover:text-navy transition-colors" data-testid={`contact-email-${e.key}`}>
                      {e.address}
                    </a>
                    <span className="block text-xs text-navy/50 mt-0.5">{e.label[lang]}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="contact-form">
                <div>
                  <label htmlFor="first_name" className="block text-xs uppercase tracking-[0.15em] font-medium text-navy/60 mb-2">{f.firstName} *</label>
                  <input id="first_name" required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="contact-first-name-input" autoComplete="given-name" />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-xs uppercase tracking-[0.15em] font-medium text-navy/60 mb-2">{f.lastName} *</label>
                  <input id="last_name" required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="contact-last-name-input" autoComplete="family-name" />
                </div>
                <div>
                  <label htmlFor="organization" className="block text-xs uppercase tracking-[0.15em] font-medium text-navy/60 mb-2">{f.organization} *</label>
                  <input id="organization" required value={form.organization} onChange={set("organization")} className={inputCls} data-testid="contact-organization-input" autoComplete="organization" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-[0.15em] font-medium text-navy/60 mb-2">{f.email} *</label>
                  <input id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="country" className="block text-xs uppercase tracking-[0.15em] font-medium text-navy/60 mb-2">{f.country} *</label>
                  <input id="country" required value={form.country} onChange={set("country")} className={inputCls} data-testid="contact-country-input" autoComplete="country-name" />
                </div>
                <div>
                  <label htmlFor="service" className="block text-xs uppercase tracking-[0.15em] font-medium text-navy/60 mb-2">{f.service} *</label>
                  <select id="service" required value={form.service} onChange={set("service")} className={inputCls} data-testid="contact-service-select">
                    <option value="" disabled>{f.selectPlaceholder}</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-xs uppercase tracking-[0.15em] font-medium text-navy/60 mb-2">{f.message} *</label>
                  <textarea id="message" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} data-testid="contact-message-textarea" />
                </div>
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                  <label htmlFor="website">Website</label>
                  <input id="website" type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" data-testid="contact-honeypot" />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="bg-navy text-white text-sm font-medium px-10 py-4 hover:bg-ser-purple transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sending ? f.sending : f.submit}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
